import { useMe } from './auth';
import { useWorkspaceMembers } from './workspaces';

export type WorkspaceRole = 'owner' | 'admin' | 'member';

export interface MyWorkspaceMembership {
  /** Current user ULID, or `null` while `useMe` is still loading. */
  userId: string | null;
  role: WorkspaceRole | null;
  /** True only for `owner`. */
  isOwner: boolean;
  /** True for `owner` and `admin`. The right gate for "can edit
   *  workspace-level config / delete other users' content / manage
   *  members" decisions. */
  isAdmin: boolean;
  /** True if the current user has *any* role in this workspace. */
  isMember: boolean;
  /** True while either underlying query is still resolving — let
   *  callers show a "loading" state rather than rendering as if the
   *  user has no role yet. */
  isLoading: boolean;
}

interface MeShape {
  data?: { id?: string };
}

interface MemberShape {
  id: string;
  attributes?: { role?: WorkspaceRole };
}

interface MembersPayload {
  data?: MemberShape[];
}

/**
 * "What role does the current user hold in *this* workspace?" — the
 * lookup that every screen gating on owner/admin re-implemented before
 * this helper. Returns a stable shape so callers don't have to thread
 * `useMe` and `useWorkspaceMembers` through their own narrowing.
 *
 * Both underlying queries are TanStack-Query-cached, so calling this
 * hook from multiple components on the same page is free.
 */
export const useMyWorkspaceMembership = (
  workspaceSlug: string | undefined,
): MyWorkspaceMembership => {
  const me = useMe();
  const members = useWorkspaceMembers(workspaceSlug);

  const userId = (me.data as MeShape | undefined)?.data?.id ?? null;
  const list = (members.data as MembersPayload | undefined)?.data ?? [];

  const myRow = userId ? list.find((m) => m.id === userId) : undefined;
  const role = myRow?.attributes?.role ?? null;

  return {
    userId,
    role,
    isOwner: role === 'owner',
    isAdmin: role === 'owner' || role === 'admin',
    isMember: Boolean(myRow),
    isLoading: me.isLoading || members.isLoading,
  };
};

export interface FormerMemberCheck {
  /** True when `userId` resolves to someone NOT in the workspace's
   *  current member list. Used by surfaces that render historical
   *  attribution (comments, activity, time entries) to add a
   *  "(former)" suffix when the original author has since left. */
  isFormer: boolean;
  /** True while the underlying members query is still loading. UI
   *  should treat the former-state as unknown until this clears
   *  (don't flash the badge on initial paint). */
  isLoading: boolean;
}

/**
 * "Is this user_id no longer a workspace member?" — answers the
 * question every audit-style surface needs when rendering an
 * orphaned attribution. Returns `false` while the members query is
 * loading so callers don't briefly flash the badge.
 *
 * Backed by the same `useWorkspaceMembers` query the People page
 * uses, so calling this from many rows on a single page is free
 * (TanStack Query dedupes by key).
 */
export const useIsFormerMember = (
  workspaceSlug: string | undefined,
  userId: string | null | undefined,
): FormerMemberCheck => {
  const members = useWorkspaceMembers(workspaceSlug);

  if (!userId || members.isLoading) {
    return { isFormer: false, isLoading: members.isLoading };
  }

  const list = (members.data as MembersPayload | undefined)?.data ?? [];
  const present = list.some((m) => m.id === userId);

  return { isFormer: !present, isLoading: false };
};
